import styled from 'styled-components'

import { ResponsiveImage } from '@syconium/little-miss-figgy'

import {
  EMBROIDERY_ICON_FILTER_ID,
  EMBROIDERY_IMAGE_FILTER_ID,
  EMBROIDERY_TEXT_FILTER_ID,
} from '../EmbroideryThreadFilter'

// Mark sizes. On the garment they follow the image's width. The close-up uses the design's sizes
// for its 640px preview, shrinking with the preview's width below that.
export const garmentSizes = `
  --graphic-gap: 0.6cqw;
  --graphic-placeholder-height: 6cqw;
  --graphic-placeholder-width: 8cqw;
  --icon-size: clamp(8px, 3.5cqw, 48px);
  --logo-height: min(11.5cqw, 71px);
  --logo-width: min(15.3cqw, 95px);
  --text-size: clamp(8px, 1.9cqw, 16px);
`

// The flatlay offset scale is per placement, not per mark type, so sleeve logos size off the icon
// instead of the chest logo. Stitched they're 2.5x an icon; design shrank the preview ~15% (2.125).
export const garmentSleeveLogoSizes = `
  --logo-height: calc(var(--icon-size) * 2.125);
  --logo-width: calc(var(--logo-height) * 4 / 3);
`

export const closeUpSizes = `
  --graphic-gap: min(5cqw, 32px);
  --graphic-placeholder-height: min(21.875cqw, 140px);
  --graphic-placeholder-width: min(21.875cqw, 140px);
  --icon-size: min(21.875cqw, 140px);
  --logo-height: min(51cqw, 326px);
  --logo-width: min(62.5cqw, 400px);
  --text-size: min(6.25cqw, 40px);
`

export const Group = styled.div<{
  $isLightGarment: boolean
  $isCloseUp: boolean
  $isSleeve: boolean
  $placementScale: number
}>`
  ${o => (o.$isCloseUp ? closeUpSizes : garmentSizes)}
  ${o => !o.$isCloseUp && o.$isSleeve && garmentSleeveLogoSizes}
  /* The flatlay offset scale. Text and logos read it; icons keep their base size. */
  --placement-scale: ${o => o.$placementScale};
  --placeholder-stroke: ${o =>
    o.$isLightGarment ? o.theme.color.text.primary.on.background : 'white'};
  --warning-stroke: ${o =>
    o.$isLightGarment ? o.theme.color.stroke.caution.text.on.background : '#FFC500'};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  z-index: ${o => o.theme.zIndex.foregroundContent};
`

// Above any text sharing the placement. Hung out of the flow when centering on the text, so the
// text alone decides the centering.
export const GraphicSlot = styled.div<{ $withText: boolean; $hangAboveText: boolean }>`
  display: flex;
  justify-content: center;
  order: -1;

  ${o => o.$withText && 'margin-bottom: var(--graphic-gap);'}

  ${o =>
    o.$withText &&
    o.$hangAboveText &&
    `
    bottom: 100%;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
  `}
`

export const Text = styled.div<{
  $color: string
  $fontFamily: string
  $fontScale: number
  $warning: boolean
  $showThreadEffect: boolean
}>`
  color: ${o => o.$color.toLowerCase()};
  display: flex;
  filter: ${o => (o.$showThreadEffect ? `url(#${EMBROIDERY_TEXT_FILTER_ID})` : 'none')};
  flex-direction: column;
  font-family: ${o => o.$fontFamily};
  font-size: calc(var(--text-size) * ${o => o.$fontScale} * var(--placement-scale));
  font-weight: bold;
  text-align: center;
  white-space: nowrap;

  ${o =>
    o.$warning &&
    `
    background: rgb(from ${o.theme.color.fill.caution.subtle} r g b / 0.3);
    border: 1px dashed var(--warning-stroke);
    border-radius: 2px;
    padding: ${o.theme.spacing(1)};
  `}
`

export const GraphicPlaceholder = styled.div`
  border: 1px dashed var(--placeholder-stroke);
  height: var(--graphic-placeholder-height);
  width: var(--graphic-placeholder-width);
`

// TODO: The scaling in the flatlay offsets is done per placement. Until this gets updated to
// per embroidery type, we won't scale the icons since the size difference it so noticeable.
// We should come back to this after that work is completed.
export const IconImage = styled(ResponsiveImage)<{ $scale: number; $showThreadEffect: boolean }>`
  filter: ${o => (o.$showThreadEffect ? `url(#${EMBROIDERY_ICON_FILTER_ID})` : 'none')};
  height: calc(var(--icon-size) * ${o => o.$scale});
  /* Override ResponsiveImage's max-width: 100%, which caps the mark to the text's width when hung above it. */
  max-width: none;
  width: calc(var(--icon-size) * ${o => o.$scale});
`

export const LogoImage = styled(ResponsiveImage)<{
  $grayscale: boolean
  $showThreadEffect: boolean
}>`
  display: block;
  filter: ${o =>
    [
      o.$showThreadEffect ? `url(#${EMBROIDERY_IMAGE_FILTER_ID})` : '',
      o.$grayscale ? 'grayscale(1)' : '',
    ]
      .join(' ')
      .trim() || 'none'};
  height: calc(var(--logo-height) * var(--placement-scale));
  /* Override ResponsiveImage's max-width: 100%, which caps the mark to the text's width when hung above it. */
  max-width: none;
  mix-blend-mode: multiply;
  object-fit: contain;
  width: calc(var(--logo-width) * var(--placement-scale));
`
